import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { api } from '../api.js';

function money(amount, currency, compact = false) {
  if (amount == null || Number.isNaN(amount)) return '—';
  return new Intl.NumberFormat('fr-CA', {
    style: 'currency', currency: currency || 'CAD',
    maximumFractionDigits: compact ? 0 : 2,
  }).format(Number(amount));
}

function StatCard({ label, value, sublabel, accent = false }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <div className={`text-2xl font-semibold tabular-nums ${accent ? 'text-cyan-400' : 'text-zinc-100'}`}>{value}</div>
      {sublabel && <div className="text-xs text-zinc-500 mt-1">{sublabel}</div>}
    </div>
  );
}

// Horizontal bar — width relative to the max in its group. No chart lib.
function Bar({ label, value, max, display, tone = 'cyan' }) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  const tones = {
    cyan: 'bg-cyan-500/70',
    amber: 'bg-amber-500/70',
    emerald: 'bg-emerald-500/70',
    zinc: 'bg-zinc-600',
  };
  return (
    <div className="flex items-center gap-3 text-sm">
      <div className="w-40 shrink-0 truncate text-zinc-300" title={label}>{label}</div>
      <div className="flex-1 h-5 rounded bg-zinc-900 overflow-hidden">
        <div className={`h-full ${tones[tone] || tones.cyan}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="w-32 shrink-0 text-right tabular-nums text-zinc-400">{display}</div>
    </div>
  );
}

function Panel({ title, caption, children }) {
  return (
    <div className="card p-4 space-y-3">
      <div>
        <h2 className="text-sm font-semibold">{title}</h2>
        {caption && <p className="text-[11px] text-zinc-500 mt-0.5">{caption}</p>}
      </div>
      {children}
    </div>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/stats')
      .then((res) => setStats(res.data))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !stats) return <div className="px-8 py-6 text-zinc-500 text-sm">{t('common.loading')}</div>;

  const fyMax = Math.max(1, ...stats.byFiscalYear.map((r) => r.CAD));
  const concMax = Math.max(1, ...stats.byConcession.map((r) => r.CAD));
  const statusTones = { ORDERED: 'cyan', RECEIVED: 'emerald', DRAFT: 'zinc', CANCELLED: 'zinc' };
  const statusMax = Math.max(1, ...Object.values(stats.byStatus));

  return (
    <div className="px-8 py-6 space-y-6">
      <header>
        <h1 className="text-xl font-semibold tracking-tight">{t('dashboard.title')}</h1>
        <p className="text-sm text-zinc-500 mt-0.5">{t('dashboard.subtitle')}</p>
      </header>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          label={t('dashboard.totalSpend')}
          value={money(stats.totals.CAD, 'CAD', true)}
          sublabel={stats.totals.USD > 0 ? `+ ${money(stats.totals.USD, 'USD', true)}` : t('dashboard.poCount', { count: stats.totalPOs })}
          accent
        />
        <StatCard
          label={t('dashboard.pending')}
          value={stats.pending.count}
          sublabel={stats.pending.CAD > 0 ? money(stats.pending.CAD, 'CAD', true) : null}
        />
        <StatCard
          label={t('dashboard.subMonthly')}
          value={money(stats.subscriptions.monthly.CAD, 'CAD', true)}
          sublabel={t('dashboard.subActive', { count: stats.subscriptions.activeCount })}
        />
        <StatCard
          label={t('dashboard.offBudget')}
          value={money(stats.offBudget.CAD, 'CAD', true)}
          sublabel={stats.offBudget.count > 0 ? t('dashboard.poCount', { count: stats.offBudget.count }) : null}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title={t('dashboard.byFiscalYear')} caption={t('dashboard.fyCaption', { month: stats.fiscalYearStartMonth })}>
          <div className="space-y-2">
            {stats.byFiscalYear.length === 0 && <p className="text-xs text-zinc-500">{t('dashboard.empty')}</p>}
            {stats.byFiscalYear.map((r) => (
              <Bar
                key={r.fy}
                label={t('dashboard.fyLabel', { year: r.fy })}
                value={r.CAD}
                max={fyMax}
                display={money(r.CAD, 'CAD', true) + (r.USD > 0 ? ` · ${money(r.USD, 'USD', true)}` : '')}
              />
            ))}
          </div>
        </Panel>

        <Panel title={t('dashboard.byStatus')}>
          <div className="space-y-2">
            {Object.entries(stats.byStatus).map(([s, n]) => (
              <Bar key={s} label={t(`po.status.${s}`)} value={n} max={statusMax} display={String(n)} tone={statusTones[s] || 'zinc'} />
            ))}
          </div>
          <div className="pt-2 mt-2 border-t border-zinc-800 grid grid-cols-2 gap-2 text-xs">
            {Object.entries(stats.byApproval).map(([s, n]) => (
              <div key={s} className="flex justify-between">
                <span className="text-zinc-500">{t(`po.approval.${s}`)}</span>
                <span className="tabular-nums text-zinc-300">{n}</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <Panel title={t('dashboard.byConcession')} caption={t('dashboard.concessionCaption')}>
        <div className="space-y-2">
          {stats.byConcession.length === 0 && <p className="text-xs text-zinc-500">{t('dashboard.empty')}</p>}
          {stats.byConcession.map((r) => (
            <Bar
              key={r.id}
              label={`${r.code} · ${r.name}`}
              value={r.CAD}
              max={concMax}
              display={money(r.CAD, 'CAD', true) + (r.USD > 0 ? ` · ${money(r.USD, 'USD', true)}` : '')}
            />
          ))}
        </div>
        {stats.unknownAmount > 0 && (
          <p className="text-[11px] text-amber-400/80 pt-1">{t('dashboard.unknownNote', { count: stats.unknownAmount })}</p>
        )}
      </Panel>
    </div>
  );
}
